import axios from 'services/axios';
import { makeAutoObservable, runInAction } from 'mobx';
import { persistIntegration } from './Valet';
import { Token, DeepAction, DeepActionResult } from './';
import { formatToken } from 'services/tokenHandler';
import { getStorage, setStorage, getSearchParam, removeFromStorage } from './Browser';
import { setAmplitudeUserDetails } from 'services/amplitudeHelper';
import RootModel from './RootModel';
import dayjs from 'dayjs';
import localforage from 'localforage';
import { setToken } from '@cw-elements/config';
import { AxiosResponse } from 'axios';
import { APIState } from './APIState';
import { addUserAttributes, resetUserAttributes } from 'tools/datadog/datadog';
import { UNIT_SYSTEM_STORAGE_KEY } from '../modules/yield-analysis/store/useUnitSystemStore';

const CLIENT_ID = import.meta.env.VITE_APP_CROPWISE_CLIENT_ID || '';
const CLIENT_SECRET = import.meta.env.VITE_APP_CROPWISE_CLIENT_SECRET;
const CLIENT_ID_AND_SECRET = btoa(`${CLIENT_ID}:${CLIENT_SECRET}`);
const CROPWISE_AUTH_URI = import.meta.env.VITE_APP_CROPWISE_AUTH_URI || '';
const CROPWISE_TOKEN_URI = import.meta.env.VITE_APP_CROPWISE_TOKEN_URI || '';
const CROPWISE_LOGOUT_URI = import.meta.env.VITE_APP_CROPWISE_LOGOUT_URI || '';
const CROPWISE_TOKEN = 'cropwise_auth_token';
const CROPWISE_API_BASE_URL = import.meta.env.VITE_APP_CROPWISE_API_URL;
const REDIRECT_URI = `${window.origin}/authenticate`;
const CONNECT_API_DOMAIN = import.meta.env.VITE_APP_API_URL || '';
const CONNECT_AUTH_SERVICE = import.meta.env.VITE_APP_CONNECT_AUTH_SERVICE || '';

type StringOrNull = string | null;

export type Organization = {
  id: string;
  name: string;
  unit_system: string;
  country: string;
  workspace_id: string;
  source: string;
};

export interface ConnectAuth {
  visitedMapper: boolean;
  orgId: string;
  userId: string;
  partner: string;
  accountId: string;
  leafUserId: string;
}

interface ConnectV3AuthResponse {
  org_id: string;
  visited_mapper: boolean;
  user_id: string;
  setting: {
    end_date: string;
    is_active: boolean;
    start_date: string;
  };
  integration_id: string;
  created: string;
  partner: string;
  account_id: string;
  auth_config: {
    access_token: string;
    refresh_token: string;
    token_created: string;
    partner_id: string;
    expires_in: string;
  };
  leaf_user_id: string;
  modified: string;
}

export const generateLoginUri = () => {
  const loginUri = new URL(CROPWISE_AUTH_URI);
  loginUri.searchParams.append('client_id', CLIENT_ID);
  loginUri.searchParams.append('redirect_uri', generateRedirect());
  loginUri.searchParams.append('response_type', 'code');
  return loginUri;
};

export const navigateToCropwiseLoginPostLogout = () => {
  const logoutUri = new URL(CROPWISE_LOGOUT_URI);
  resetUserAttributes();
  const loginUri = generateLoginUri();
  logoutUri.searchParams.append('continue', loginUri.toString());
  window.location.assign(logoutUri.toString());
};

export const generateRedirect = () => {
  const pathname = window.location.pathname.replace('/', '');
  const search = new URLSearchParams(window.location.search);
  const has_code = !!search.get('code');
  let query_pathname = search.get('pathname');
  let search_string = '';
  if (has_code && query_pathname) {
    search_string = `?pathname=${query_pathname}`;
  } else if (!has_code && pathname) {
    search_string = `?pathname=${pathname}`;
  }
  return `${REDIRECT_URI}${search_string}`;
};

export const navigateToCropwiseLogin = () => {
  const loginUri = generateLoginUri();
  window.location.assign(loginUri.toString());
};

export const check_for_deep_action = () => {
  const prev_deep_action: DeepAction | undefined = getStorage('deep_action');
  const search = new URLSearchParams(window.location.search);
  const org_id: string | null = search.get('org_id');
  const callback_uri: string | null = search.get('callback_uri');
  if (!org_id || !callback_uri) return prev_deep_action;
  const state: string | null = search.get('state');
  const deep_action: DeepAction = {
    org_id,
    callback_uri,
    state,
  };
  setStorage('deep_action', deep_action);
  return deep_action;
};

export const handle_deep_action_redirect = (result: DeepActionResult, error_code?: string) => {
  const deep_action: DeepAction = getStorage('deep_action');
  const callback_uri = deep_action.callback_uri;
  const new_search = new URLSearchParams();
  new_search.append('result', result.toLowerCase());
  new_search.append('state', deep_action.state ?? '');
  if (error_code) {
    new_search.append('error_code', error_code.toString());
  }
  removeFromStorage('deep_action');
  window.location.assign(`${decodeURIComponent(callback_uri)}?${new_search.toString()}`);
};

export const initAuth = async () => {
  let cropwiseToken = await checkForLocalAuthToken();
  if (cropwiseToken) {
    return cropwiseToken;
  }
  const authCode = getSearchParam('code');
  if (!authCode) {
    navigateToCropwiseLogin();
    return { error_type: 'NO_CODE' };
  }
  return getTokenWithCode(authCode);
};

export const checkForLocalAuthToken = () => {
  const token = window.localStorage.getItem(CROPWISE_TOKEN);
  if (!token) return;
  return JSON.parse(token);
};

export const clearLocalAuthToken = () => {
  window.localStorage.removeItem(CROPWISE_TOKEN);
};

export const clearSessionStorage = () => {
  window.sessionStorage.clear();
};

export const clearIndexedDB = () => {
  const dbTime = localStorage.getItem('db_time');
  if (dbTime) {
    const auxDbTime = dayjs(dbTime);
    const now = dayjs(new Date().toISOString());
    const diffHours = now.diff(auxDbTime, 'hour');
    if (diffHours >= 0) {
      localforage.clear();
      localStorage.setItem('db_time', new Date().toISOString());
    }
  }
};

const getTokenWithCode = async (code: string) => {
  try {
    const response = await axios.request({
      method: 'POST',
      url: CROPWISE_TOKEN_URI,
      headers: { authorization: `Basic ${CLIENT_ID_AND_SECRET}` },
      params: {
        code,
        grant_type: 'authorization_code',
        redirect_uri: generateRedirect(),
        client_id: CLIENT_ID,
      },
    });
    return response.data;
  } catch (err: any) {
    console.error('get token:', err);
    return err;
  }
};

const refreshToken = async (refresh_token: string) => {
  try {
    const response = await axios.request({
      method: 'POST',
      url: `${CROPWISE_API_BASE_URL}/oauth/token`,
      params: {
        grant_type: 'refresh_token',
        client_id: CLIENT_ID,
        client_secret: CLIENT_SECRET,
        redirect_uri: generateRedirect(),
        refresh_token,
      },
      headers: { authorization: `Basic ${CLIENT_ID_AND_SECRET}` },
    });
    return response.data;
  } catch (err: any) {
    console.error('Failed to refresh Cropwise token:', err);
    return err;
  }
};

export const getCropwiseUser = async (accessToken: string) => {
  try {
    const response: any = await axios.request({
      method: 'GET',
      url: `${CROPWISE_API_BASE_URL}/v2/accounts/me`,
      headers: { authorization: `Bearer ${accessToken}` },
    });
    if (!response?.data?.id) {
      console.error('get user info failed');
      return;
    }
    return response?.data;
  } catch (err: any) {
    if (err?.response?.status) {
      err.status = err.response.status;
      err.message = err.response.statusText;
    }
    console.error('Failed GET Cropwise User:', err);
    return err;
  }
};

export const getCropwiseOrganizations = async (accessToken: string) => {
  try {
    const response: any = await axios.request({
      method: 'GET',
      url: `${CROPWISE_API_BASE_URL}/v2/orgs?attributes=workspaces`,
      headers: { authorization: `Bearer ${accessToken}` },
    });
    if (!response?.data) {
      console.error('get user info failed');
      return;
    }
    return response.data?.content;
  } catch (err: any) {
    console.error('Failed GET Cropwise Organizations');
    return err;
  }
};

export const getConnectV3Auth = async (
  orgId: string,
  userId: string,
  partner: string = 'cropwise',
): Promise<ConnectAuth | undefined> => {
  const url = `${CONNECT_API_DOMAIN}${CONNECT_AUTH_SERVICE}/v3/authaccounts`;
  try {
    const response: AxiosResponse<ConnectV3AuthResponse> = await axios.get(url, {
      params: { org_id: orgId, user_id: userId, partner: partner },
    });
    if (response.status >= 200 && response.status < 300) {
      return {
        visitedMapper: response.data.visited_mapper,
        orgId: response.data.org_id,
        userId: response.data.user_id,
        leafUserId: response.data.leaf_user_id,
        partner: response.data.partner,
        accountId: response.data.account_id,
      };
    }
  } catch (e: any) {
    console.error(e);
  }
};

export const updateConnectAuthVisitedMapper = async (connectAuth: ConnectAuth): Promise<void> => {
  const url = `${CONNECT_API_DOMAIN}${CONNECT_AUTH_SERVICE}/v3/authaccounts`;
  try {
    await axios.patch(url, { user_id: connectAuth.userId, visited_mapper: true });
  } catch (e: any) {
    console.error(`Could not update connect auth visted mapper. Reason: ${e}`);
  }
};

type ErrorObject = {
  error_type: string;
};

function errorGuard(toBeDetermined: any): toBeDetermined is ErrorObject {
  if ((toBeDetermined as ErrorObject)?.error_type) {
    return true;
  }
  return false;
}

export class Auth {
  private _status: APIState = APIState.LOADING;
  private _token: Token = {
    access_token: '',
    refresh_token: '',
    expires_in: '',
  };
  private _mgmtZonesPermission = false;
  private _userId: StringOrNull = null;
  private _userName: StringOrNull = null;
  private _userEmail: StringOrNull = null;
  private _defaultWorkspaceId: StringOrNull = null;
  private _activeUser: string = '';
  private _activeOrganization: Organization | null = null;
  private _rootStore: RootModel;
  private _deepAction: DeepAction | undefined;
  private _connectAuth: ConnectAuth | undefined;

  constructor(rootStore: RootModel) {
    this._rootStore = rootStore;
    makeAutoObservable(this);
  }
  async login() {
    const deep_action: DeepAction | undefined = check_for_deep_action();
    let response: Token | ErrorObject = await initAuth();
    if (errorGuard(response)) {
      if (response.error_type !== 'NO_CODE') {
        this.status = APIState.FAIL;
      }
      return;
    }
    let user = await getCropwiseUser(response.access_token);
    if (user?.message === 'Unauthorized') {
      response = await refreshToken(response.refresh_token);
    }
    if (errorGuard(response)) {
      if (response?.error_type) {
        this.status = APIState.FAIL;
      }
      return;
    }
    if (user?.message === 'Unauthorized') {
      user = await getCropwiseUser(response.access_token);
    }
    if (user.error_type) {
      this.status = APIState.FAIL;
      return;
    }

    const newToken = formatToken(response);
    setToken(newToken.access_token);
    window.localStorage.setItem(CROPWISE_TOKEN, JSON.stringify(newToken));
    this._rootStore.setUserId(user?.id);
    setAmplitudeUserDetails(user);
    if (user?.id && user?.email) {
      addUserAttributes(user.id, user.email);
    }
    let localOrg = '';
    const orgs: Organization[] = await getCropwiseOrganizations(newToken.access_token);
    if (orgs.length > 0) {
      localOrg = orgs[0].id;
    }
    const connectAuth = await getConnectV3Auth(localOrg, user?.id);
    runInAction(() => {
      this._token = newToken;
      this._status = APIState.SUCCESS;
      this._userId = user?.id;
      this._userName = user?.name;
      this._rootStore.userEmail = user?.email;
      this._userEmail = user?.email;
      this._defaultWorkspaceId = user?.default_workspace_id;
      this._deepAction = deep_action;
      this._connectAuth = connectAuth;
    });
  }

  logout() {
    clearLocalAuthToken();
    navigateToCropwiseLoginPostLogout();
    clearSessionStorage();
    clearIndexedDB();
  }

  async setActiveUser(activeUser: string) {
    runInAction(() => {
      this._activeUser = activeUser;
    });
    if (!this.token) return;
    return persistIntegration(
      this._rootStore.userId,
      this._rootStore.orgId,
      'cropwise',
      undefined,
      this.token,
    );
  }

  changeUnitSystem(unitSystem: string) {
    runInAction(() => {
      this._rootStore.changeUnitSystem(unitSystem);
    });
  }

  get status(): APIState {
    return this._status;
  }
  set status(newStatus: APIState) {
    runInAction(() => {
      this._status = newStatus;
    });
  }
  get token(): Token {
    return this._token;
  }
  set token(token: Token) {
    this._token = token;
    if (token) {
      this._status = APIState.SUCCESS;
    }
  }
  get activeOrganization(): Organization | null {
    return this._activeOrganization;
  }
  set activeOrganization(organization: Organization | null) {
    if (!organization) return;
    setStorage('activeOrg', organization);
    const defaultUnitSystem = getStorage(UNIT_SYSTEM_STORAGE_KEY) ?? organization.unit_system;
    this._rootStore.setOrgInformation(organization.id, organization.name, defaultUnitSystem);
    this._rootStore.integrations.organizationCountryCode = organization.country;
    runInAction(() => {
      this._activeOrganization = organization;
    });
  }

  get userId(): StringOrNull {
    return this._userId;
  }
  get userName(): StringOrNull {
    return this._userName;
  }
  get userEmail(): StringOrNull {
    return this._userEmail;
  }

  get defaultWorkspaceId(): StringOrNull {
    return this._defaultWorkspaceId;
  }

  get activeUser(): StringOrNull {
    return this._activeUser;
  }
  get mgmtZonesPermission(): boolean {
    return this._mgmtZonesPermission;
  }
  get deepAction(): DeepAction | undefined {
    return this._deepAction;
  }

  get connectAuth(): ConnectAuth | undefined {
    return this._connectAuth;
  }

  async organizations(deepActionOrgId?: string | null): Promise<Organization[]> {
    let orgs = await getCropwiseOrganizations(this.token.access_token || '');
    if (orgs.error_type === 'invalid_token') {
      const newToken = await this.refreshToken();
      orgs = await getCropwiseOrganizations(newToken?.access_token || '');
    }
    if (orgs.error_type) return Promise.resolve([]);

    let activeOrg = getStorage('activeOrg');
    if (!activeOrg) {
      activeOrg = orgs[0];
    }

    const searchParamOrgId = getSearchParam('org_id');

    let active_org_id: string | null;
    if (deepActionOrgId) {
      active_org_id = deepActionOrgId;
    } else if (searchParamOrgId) {
      active_org_id = searchParamOrgId;
    } else {
      active_org_id = activeOrg.id;
    }

    if (active_org_id) {
      const getOrgById = orgs.find((org: any) => org.id === active_org_id);
      if (getOrgById) {
        activeOrg = getOrgById;
      } else {
        const newURL = window.location.href.split('?')[0];
        window.history.pushState('object', document.title, newURL);
      }
    }

    runInAction(() => {
      this.activeOrganization = activeOrg;
    });

    return orgs;
  }

  async refreshToken() {
    const token = await refreshToken(this.token.refresh_token);
    if (token?.error_type) {
      console.error('failed to refresh token: ', token);
      return;
    }
    runInAction(() => {
      this._token = token;
      this._status = APIState.SUCCESS;
    });
    return token;
  }

  setSelectedFarmId(id: string) {
    runInAction(() => {
      this._rootStore.setSelectedFarmId(id);
    });
  }

  setFirstFarmId(id: string) {
    runInAction(() => {
      this._rootStore.setFirstFarmId(id);
    });
  }

  setConnectAuth(connectAuth: ConnectAuth): void {
    runInAction(() => {
      this._connectAuth = connectAuth;
    });
  }

  complete_deep_action(value: DeepActionResult, error_code?: string) {
    handle_deep_action_redirect(value, error_code);
  }
}
